import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger,
  DialogFooter 
} from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Mail, 
  Send, 
  MessageSquare, 
  Users,
  Clock,
  CheckCircle,
  Zap,
  Phone,
  Settings,
  Play,
  Pause,
  Calendar,
  Bell
} from 'lucide-react';
import { toast } from 'sonner';

// ICRCL Contact Details
const ICRCL_EMAIL = "info@icrcl.co.uk";
const ICRCL_PHONE = "+447380350059";
const ICRCL_ADDRESS = "City Gate House, 246-250 Romford Road, London, E7 9HZ";

export default function MessagingPage() {
  const { api, user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [patients, setPatients] = useState([]);
  const [staff, setStaff] = useState([]);
  const [automations, setAutomations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [composeDialogOpen, setComposeDialogOpen] = useState(false);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [automationDialogOpen, setAutomationDialogOpen] = useState(false);
  const [selectedPatients, setSelectedPatients] = useState([]);
  
  const [composeForm, setComposeForm] = useState({
    recipient_type: 'patient',
    recipient_id: '',
    subject: '',
    message: '',
    message_type: 'email',
    template_id: ''
  });

  const [bulkForm, setBulkForm] = useState({
    recipient_group: 'all_patients',
    subject: '',
    message: '',
    message_type: 'email',
    schedule_time: ''
  });

  const [automationForm, setAutomationForm] = useState({
    name: '',
    trigger: 'appointment_reminder',
    timing: '24_hours_before',
    message_type: 'email',
    subject: '',
    message: '',
    is_active: true
  });

  const messageTemplates = [
    { id: '1', name: 'Appointment Reminder', subject: 'Appointment Reminder - ICRCL', body: `Dear {patient_name},\n\nThis is a reminder about your upcoming appointment at ICRCL on {date} at {time}.\n\nLocation: ${ICRCL_ADDRESS}\n\nPlease arrive 10 minutes early.\n\nIf you need to reschedule, please contact us at ${ICRCL_PHONE} or ${ICRCL_EMAIL}\n\nBest regards,\nICRCL Team` },
    { id: '2', name: 'Assessment Report Ready', subject: 'Your Assessment Report is Ready - ICRCL', body: `Dear {parent_name},\n\nThe assessment report for {patient_name} is now ready.\n\nYou can:\n- Log into the parent portal to view it\n- Contact us to arrange collection\n- Request it to be emailed\n\nContact: ${ICRCL_EMAIL} | ${ICRCL_PHONE}\n\nBest regards,\nICRCL Team` },
    { id: '3', name: 'Invoice Notification', subject: 'Invoice from ICRCL', body: `Dear {parent_name},\n\nPlease find attached your invoice for services provided to {patient_name}.\n\nPayment Methods:\n- Bank Transfer (details on invoice)\n- Contact us for card payment\n\nQueries: ${ICRCL_EMAIL}\n\nBest regards,\nICRCL Finance Team` },
    { id: '4', name: 'Session Summary', subject: 'Therapy Session Summary - {date}', body: `Dear {parent_name},\n\nHere is a summary of today's therapy session for {patient_name}.\n\nSession Notes:\n{session_notes}\n\nHome Activities:\n{home_activities}\n\nNext Appointment: {next_appointment}\n\nQuestions? Contact: ${ICRCL_EMAIL}\n\nBest regards,\n{therapist_name}\nICRCL` },
    { id: '5', name: 'Welcome New Patient', subject: 'Welcome to ICRCL', body: `Dear {parent_name},\n\nWelcome to Innovative Care and Rehab Centre Limited (ICRCL)!\n\nWe're delighted to have {patient_name} join us. Here's what to expect:\n\n1. Initial Assessment: Your therapist will conduct a comprehensive evaluation\n2. Treatment Plan: We'll create a personalized therapy plan\n3. Regular Sessions: Ongoing therapy tailored to your child's needs\n\nOur Location:\n${ICRCL_ADDRESS}\n\nContact Us:\nPhone: ${ICRCL_PHONE}\nEmail: ${ICRCL_EMAIL}\n\nWe look forward to working with you!\n\nBest regards,\nThe ICRCL Team` }
  ];

  const automationTriggers = [
    { id: 'appointment_reminder', name: 'Appointment Reminder', description: 'Send reminder before appointments' },
    { id: 'appointment_followup', name: 'Post-Appointment Follow-up', description: 'Send follow-up after appointments' },
    { id: 'birthday_greeting', name: 'Birthday Greeting', description: 'Send birthday wishes to patients' },
    { id: 'assessment_complete', name: 'Assessment Complete', description: 'Notify when assessment is ready' },
    { id: 'invoice_due', name: 'Invoice Due Reminder', description: 'Remind about pending invoices' },
    { id: 'inactive_patient', name: 'Inactive Patient Check-in', description: 'Check in with patients who haven\'t visited' }
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [messagesRes, patientsRes, staffRes, automationsRes] = await Promise.all([
        api.get('/messages').catch(() => ({ data: [] })),
        api.get('/patients'),
        api.get('/staff'),
        api.get('/automations').catch(() => ({ data: [] }))
      ]);
      setMessages(messagesRes.data);
      setPatients(patientsRes.data);
      setStaff(staffRes.data);
      setAutomations(automationsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTemplate = (templateId) => {
    const template = messageTemplates.find(t => t.id === templateId);
    if (template) {
      setComposeForm({
        ...composeForm,
        template_id: templateId,
        subject: template.subject,
        message: template.body
      });
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    try {
      await api.post('/messages', {
        ...composeForm,
        sender_id: user.id,
        sender_email: ICRCL_EMAIL,
        sender_phone: ICRCL_PHONE
      });
      toast.success('Message sent successfully from ' + (composeForm.message_type === 'email' ? ICRCL_EMAIL : ICRCL_PHONE));
      setComposeDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
    }
  };

  const handleSendBulk = async (e) => {
    e.preventDefault();
    try {
      await api.post('/messages/bulk', {
        ...bulkForm,
        sender_email: ICRCL_EMAIL,
        sender_phone: ICRCL_PHONE,
        selected_patients: selectedPatients.length > 0 ? selectedPatients : null
      });
      toast.success(`Bulk ${bulkForm.message_type} queued for ${bulkForm.recipient_group === 'selected' ? selectedPatients.length : 'all'} recipients`);
      setBulkDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send bulk message');
    }
  };

  const handleSaveAutomation = async (e) => {
    e.preventDefault();
    try {
      await api.post('/automations', automationForm);
      toast.success('Automation saved and activated');
      setAutomationDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save automation');
    }
  };

  const toggleAutomation = async (automationId, isActive) => {
    try {
      await api.put(`/automations/${automationId}/toggle`, { is_active: !isActive });
      toast.success(`Automation ${!isActive ? 'activated' : 'paused'}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update automation');
    }
  };

  const togglePatientSelection = (patientId) => {
    setSelectedPatients(prev => 
      prev.includes(patientId) 
        ? prev.filter(id => id !== patientId)
        : [...prev, patientId]
    );
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#1e3a5f]">Messages & Automation</h1>
          <p className="text-slate-500 mt-1">
            Email from <span className="font-medium text-[#1e3a5f]">{ICRCL_EMAIL}</span> | 
            SMS from <span className="font-medium text-[#1e3a5f]">{ICRCL_PHONE}</span>
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Dialog open={automationDialogOpen} onOpenChange={setAutomationDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="rounded-full">
                <Zap className="w-4 h-4 mr-2" />
                New Automation
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle className="text-[#1e3a5f]">Create Automation</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSaveAutomation} className="space-y-4">
                <div className="space-y-2">
                  <Label>Automation Name *</Label>
                  <Input value={automationForm.name} onChange={(e) => setAutomationForm({...automationForm, name: e.target.value})} placeholder="e.g., 24hr Appointment Reminder" required />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Trigger Event</Label>
                    <Select value={automationForm.trigger} onValueChange={(v) => setAutomationForm({...automationForm, trigger: v})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {automationTriggers.map(t => (
                          <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Timing</Label>
                    <Select value={automationForm.timing} onValueChange={(v) => setAutomationForm({...automationForm, timing: v})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="immediately">Immediately</SelectItem>
                        <SelectItem value="1_hour_before">1 Hour Before</SelectItem>
                        <SelectItem value="24_hours_before">24 Hours Before</SelectItem>
                        <SelectItem value="48_hours_before">48 Hours Before</SelectItem>
                        <SelectItem value="1_week_before">1 Week Before</SelectItem>
                        <SelectItem value="1_hour_after">1 Hour After</SelectItem>
                        <SelectItem value="24_hours_after">24 Hours After</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Message Type</Label>
                  <Select value={automationForm.message_type} onValueChange={(v) => setAutomationForm({...automationForm, message_type: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="email">Email (from {ICRCL_EMAIL})</SelectItem>
                      <SelectItem value="sms">SMS (from {ICRCL_PHONE})</SelectItem>
                      <SelectItem value="both">Both Email & SMS</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Subject (for email)</Label>
                  <Input value={automationForm.subject} onChange={(e) => setAutomationForm({...automationForm, subject: e.target.value})} />
                </div>
                <div className="space-y-2">
                  <Label>Message</Label>
                  <Textarea value={automationForm.message} onChange={(e) => setAutomationForm({...automationForm, message: e.target.value})} rows={5} placeholder="Use {patient_name}, {date}, {time}, {therapist_name} for personalization" />
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={automationForm.is_active} onCheckedChange={(v) => setAutomationForm({...automationForm, is_active: v})} />
                  <Label>Activate immediately</Label>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setAutomationDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" className="bg-[#1e3a5f]">Save Automation</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog open={bulkDialogOpen} onOpenChange={setBulkDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="rounded-full">
                <Users className="w-4 h-4 mr-2" />
                Bulk Message
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-[#1e3a5f]">Send Bulk Message</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSendBulk} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Recipients</Label>
                    <Select value={bulkForm.recipient_group} onValueChange={(v) => setBulkForm({...bulkForm, recipient_group: v})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all_patients">All Patients/Families</SelectItem>
                        <SelectItem value="all_staff">All Staff</SelectItem>
                        <SelectItem value="active_patients">Active Patients Only</SelectItem>
                        <SelectItem value="selected">Select Specific Patients</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Message Type</Label>
                    <Select value={bulkForm.message_type} onValueChange={(v) => setBulkForm({...bulkForm, message_type: v})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="email">Email (from {ICRCL_EMAIL})</SelectItem>
                        <SelectItem value="sms">SMS (from {ICRCL_PHONE})</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {bulkForm.recipient_group === 'selected' && (
                  <div className="space-y-2">
                    <Label>Select Patients ({selectedPatients.length} selected)</Label>
                    <div className="max-h-40 overflow-y-auto border rounded-lg p-2 space-y-1">
                      {patients.map(p => (
                        <div key={p.id} className="flex items-center gap-2 p-2 hover:bg-slate-50 rounded">
                          <Checkbox 
                            checked={selectedPatients.includes(p.id)}
                            onCheckedChange={() => togglePatientSelection(p.id)}
                          />
                          <span>{p.first_name} {p.last_name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Subject</Label>
                  <Input value={bulkForm.subject} onChange={(e) => setBulkForm({...bulkForm, subject: e.target.value})} required />
                </div>
                <div className="space-y-2">
                  <Label>Message</Label>
                  <Textarea value={bulkForm.message} onChange={(e) => setBulkForm({...bulkForm, message: e.target.value})} rows={6} required />
                </div>
                <div className="space-y-2">
                  <Label>Schedule (optional)</Label>
                  <Input type="datetime-local" value={bulkForm.schedule_time} onChange={(e) => setBulkForm({...bulkForm, schedule_time: e.target.value})} />
                  <p className="text-xs text-slate-500">Leave empty to send immediately</p>
                </div>

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setBulkDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" className="bg-[#2a9d8f]">
                    <Send className="w-4 h-4 mr-2" />
                    Send to {bulkForm.recipient_group === 'selected' ? selectedPatients.length : 'All'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog open={composeDialogOpen} onOpenChange={setComposeDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#1e3a5f] hover:bg-[#1e3a5f]/90 rounded-full" data-testid="compose-message-btn">
                <Mail className="w-4 h-4 mr-2" />
                Compose
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle className="text-[#1e3a5f]">Compose Message</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSendMessage} className="space-y-4">
                <div className="bg-blue-50 p-3 rounded-lg text-sm">
                  <p className="text-blue-700">
                    <strong>From:</strong> {composeForm.message_type === 'email' ? ICRCL_EMAIL : ICRCL_PHONE}
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Send To</Label>
                    <Select value={composeForm.recipient_type} onValueChange={(v) => setComposeForm({...composeForm, recipient_type: v, recipient_id: ''})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="patient">Patient/Family</SelectItem>
                        <SelectItem value="staff">Staff Member</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Select Recipient</Label>
                    <Select value={composeForm.recipient_id} onValueChange={(v) => setComposeForm({...composeForm, recipient_id: v})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select..." />
                      </SelectTrigger>
                      <SelectContent>
                        {(composeForm.recipient_type === 'patient' ? patients : staff).map(p => (
                          <SelectItem key={p.id} value={p.id}>{p.first_name} {p.last_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Message Type</Label>
                    <Select value={composeForm.message_type} onValueChange={(v) => setComposeForm({...composeForm, message_type: v})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="email">Email</SelectItem>
                        <SelectItem value="sms">SMS</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Use Template</Label>
                    <Select value={composeForm.template_id} onValueChange={handleSelectTemplate}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select template..." />
                      </SelectTrigger>
                      <SelectContent>
                        {messageTemplates.map(t => (
                          <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Subject</Label>
                  <Input value={composeForm.subject} onChange={(e) => setComposeForm({...composeForm, subject: e.target.value})} required />
                </div>

                <div className="space-y-2">
                  <Label>Message</Label>
                  <Textarea value={composeForm.message} onChange={(e) => setComposeForm({...composeForm, message: e.target.value})} rows={8} required />
                </div>

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setComposeDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" className="bg-[#1e3a5f]">
                    <Send className="w-4 h-4 mr-2" />
                    Send
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Contact Info Card */}
      <Card className="card-icrcl bg-gradient-to-r from-[#1e3a5f] to-[#2a9d8f] text-white">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Mail className="w-5 h-5" />
                <span className="font-medium">{ICRCL_EMAIL}</span>
              </div>
              <div className="flex items-center gap-2">
                <Phone className="w-5 h-5" />
                <span className="font-medium">{ICRCL_PHONE}</span>
              </div>
            </div>
            <div className="text-white/80 text-sm">{ICRCL_ADDRESS}</div>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card className="card-icrcl">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Mail className="w-8 h-8 text-[#1e3a5f]" />
              <div>
                <p className="text-2xl font-bold text-[#1e3a5f]">{messages.filter(m => m.message_type === 'email').length}</p>
                <p className="text-sm text-slate-500">Emails Sent</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-icrcl">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Phone className="w-8 h-8 text-[#2a9d8f]" />
              <div>
                <p className="text-2xl font-bold text-[#2a9d8f]">{messages.filter(m => m.message_type === 'sms').length}</p>
                <p className="text-sm text-slate-500">SMS Sent</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-icrcl">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Zap className="w-8 h-8 text-amber-500" />
              <div>
                <p className="text-2xl font-bold text-amber-600">{automations.filter(a => a.is_active).length}</p>
                <p className="text-sm text-slate-500">Active Automations</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-icrcl">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Clock className="w-8 h-8 text-purple-500" />
              <div>
                <p className="text-2xl font-bold text-purple-600">{messages.filter(m => m.status === 'scheduled').length}</p>
                <p className="text-sm text-slate-500">Scheduled</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-icrcl">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-8 h-8 text-green-500" />
              <div>
                <p className="text-2xl font-bold text-green-600">{messages.filter(m => m.status === 'sent').length}</p>
                <p className="text-sm text-slate-500">Delivered</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="automations" className="space-y-6">
        <TabsList>
          <TabsTrigger value="automations">
            <Zap className="w-4 h-4 mr-2" />
            Automations
          </TabsTrigger>
          <TabsTrigger value="messages">
            <Mail className="w-4 h-4 mr-2" />
            Message History
          </TabsTrigger>
          <TabsTrigger value="templates">
            <MessageSquare className="w-4 h-4 mr-2" />
            Templates
          </TabsTrigger>
        </TabsList>

        <TabsContent value="automations">
          <Card className="card-icrcl">
            <CardHeader>
              <CardTitle className="text-lg">Message Automations</CardTitle>
              <CardDescription>Automated emails and SMS based on triggers</CardDescription>
            </CardHeader>
            <CardContent>
              {automations.length === 0 ? (
                <div className="p-8 text-center text-slate-500">
                  <Zap className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>No automations configured</p>
                  <Button variant="link" className="text-[#2a9d8f]" onClick={() => setAutomationDialogOpen(true)}>
                    Create your first automation
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {automations.map((auto) => (
                    <div key={auto.id} className={`p-4 rounded-lg border ${auto.is_active ? 'bg-green-50 border-green-200' : 'bg-slate-50 border-slate-200'}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${auto.is_active ? 'bg-green-100' : 'bg-slate-200'}`}>
                            <Zap className={`w-5 h-5 ${auto.is_active ? 'text-green-600' : 'text-slate-400'}`} />
                          </div>
                          <div>
                            <h4 className="font-medium">{auto.name}</h4>
                            <p className="text-sm text-slate-500">
                              {automationTriggers.find(t => t.id === auto.trigger)?.name} • {auto.message_type}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className={auto.is_active ? 'badge-active' : 'badge-cancelled'}>
                            {auto.is_active ? 'Active' : 'Paused'}
                          </Badge>
                          <Button variant="ghost" size="sm" onClick={() => toggleAutomation(auto.id, auto.is_active)}>
                            {auto.is_active ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Default Automations */}
              <div className="mt-6 pt-6 border-t">
                <h4 className="font-medium text-slate-700 mb-4">Recommended Automations</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {automationTriggers.map((trigger) => (
                    <div key={trigger.id} className="p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{trigger.name}</p>
                          <p className="text-sm text-slate-500">{trigger.description}</p>
                        </div>
                        <Button variant="outline" size="sm" onClick={() => {
                          setAutomationForm({...automationForm, trigger: trigger.id, name: trigger.name});
                          setAutomationDialogOpen(true);
                        }}>
                          Setup
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="messages">
          <Card className="card-icrcl">
            <CardHeader>
              <CardTitle className="text-lg">Message History</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {messages.length === 0 ? (
                <div className="p-8 text-center text-slate-500">
                  <Mail className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>No messages yet</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="table-icrcl">
                      <TableHead>To</TableHead>
                      <TableHead>Subject</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {messages.map((msg) => (
                      <TableRow key={msg.id} className="hover:bg-slate-50">
                        <TableCell className="font-medium">{msg.recipient_name}</TableCell>
                        <TableCell>{msg.subject}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="flex items-center gap-1 w-fit">
                            {msg.message_type === 'email' ? <Mail className="w-3 h-3" /> : <Phone className="w-3 h-3" />}
                            {msg.message_type}
                          </Badge>
                        </TableCell>
                        <TableCell>{new Date(msg.created_at).toLocaleDateString('en-GB')}</TableCell>
                        <TableCell>
                          <Badge className={msg.status === 'sent' ? 'badge-completed' : msg.status === 'scheduled' ? 'badge-pending' : 'badge-cancelled'}>
                            {msg.status}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="templates">
          <Card className="card-icrcl">
            <CardHeader>
              <CardTitle className="text-lg">Message Templates</CardTitle>
              <CardDescription>Pre-defined templates for common communications</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {messageTemplates.map((template) => (
                  <div key={template.id} className="p-4 bg-slate-50 rounded-lg hover:bg-slate-100">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-[#1e3a5f]">{template.name}</h4>
                      <Button variant="outline" size="sm" onClick={() => {
                        handleSelectTemplate(template.id);
                        setComposeDialogOpen(true);
                      }}>
                        Use Template
                      </Button>
                    </div>
                    <p className="text-sm text-slate-600 font-medium">{template.subject}</p>
                    <p className="text-xs text-slate-500 mt-1 line-clamp-2">{template.body}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
